import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HelpCircle } from "lucide-react";
import HelpDrawer from "./HelpDrawer";
import { fetchAvailableHelpPages } from "./helpApi";

const SEEN_KEY = "belvizyon_help_seen_pages";

function readSeen() {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function markSeen(pageId) {
  try {
    const seen = readSeen();
    if (!seen.includes(pageId)) {
      window.localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, pageId]));
    }
  } catch {
    /* localStorage kapalıysa sessizce geç — yardım yine çalışır */
  }
}

/**
 * Header'daki tek "?" butonu — açık olan sayfanın yardımını gösterir.
 *
 * Neden sidebar'da her satıra değil de burada tek buton: menüde 20 satır var;
 * satır başına ikon hem görsel gürültü hem yanlış tıklama demek. Ayrıca
 * kullanıcı yardımı sayfaya GİRMEDEN önce değil, İÇERİDEYKEN takılınca arar.
 *
 * Buton yalnız yardım metni OLAN sayfalarda görünür (backend `/help/available`
 * listesi) — metni yazılmamış sayfada boş panel açılmaz.
 */
const HelpButton = ({ pageId, onNavigate }) => {
  const [availablePages, setAvailablePages] = useState(null);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);

  useEffect(() => {
    let cancelled = false;
    fetchAvailableHelpPages()
      .then((ids) => {
        if (!cancelled) setAvailablePages(ids);
      })
      .catch(() => {
        // Yardım listesi alınamazsa buton gizlenir; ana akış etkilenmez.
        if (!cancelled) setAvailablePages([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasHelp = useMemo(
    () => Boolean(pageId) && Array.isArray(availablePages) && availablePages.includes(pageId),
    [pageId, availablePages]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (pageId) {
      markSeen(pageId);
      setSeen((prev) => (prev.includes(pageId) ? prev : [...prev, pageId]));
    }
  }, [pageId]);

  // F1 — bu sayfanın yardımını aç/kapat. Tarayıcının kendi yardımını bastırır.
  useEffect(() => {
    if (!hasHelp) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "F1") return;
      e.preventDefault();
      if (open) setOpen(false);
      else handleOpen();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasHelp, open, handleOpen]);

  if (!hasHelp) return null;

  // İlk kez bu sayfadaysa butonu hafifçe vurgula (keşif); bir kez açılınca sus.
  const isNew = !seen.includes(pageId);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        title="Bu sayfa nasıl kullanılır? (F1)"
        aria-label="Bu sayfa nasıl kullanılır"
        data-testid="help-button"
        className={`relative flex items-center justify-center h-9 w-9 rounded-full border transition-colors ${
          open
            ? "bg-green-600 border-green-600 text-white"
            : "bg-white border-gray-300 text-gray-600 hover:bg-green-50 hover:text-green-700 hover:border-green-400"
        }`}
      >
        <HelpCircle className="h-5 w-5" />
        {isNew && !open && (
          <span
            data-testid="help-new-dot"
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white animate-pulse"
          />
        )}
      </button>

      <HelpDrawer
        open={open}
        pageId={pageId}
        onClose={() => setOpen(false)}
        onNavigate={onNavigate}
      />
    </>
  );
};

export default HelpButton;
